import type { CSSProperties, HTMLAttributes, ReactNode } from 'react';

export interface AspectBoxProps extends Omit<HTMLAttributes<HTMLDivElement>, 'style' | 'children'> {
  /**
   * Width / height (4/3, 1, …). `null` sizes the box by its content (no ratio);
   * the element tree stays the same, so switching does not remount children.
   */
  ratio: number | null;
  /** Styles for the outer box (width, max-width, …). `position` is always `relative`. */
  style?: CSSProperties;
  children?: ReactNode;
}

const FILL: CSSProperties = { position: 'absolute', top: 0, right: 0, bottom: 0, left: 0 };

/**
 * A box of a fixed aspect ratio without CSS `aspect-ratio` (Safari 15 / Chrome
 * 88; our floor is Chrome 80, #981 981d, and an inline style never goes through
 * Lightning CSS). A padding-top sizer (a percentage of the box's width) gives
 * the height; the children fill the box, so `height: 100%` and absolutely
 * positioned children resolve against it as they did against an aspect-ratio
 * box. Content never stretches the box: it is clipped to the ratio by the
 * caller's `overflow` if it must be.
 */
export function AspectBox({ ratio, style, children, ...rest }: AspectBoxProps) {
  const sized = ratio !== null && Number.isFinite(ratio) && ratio > 0;
  return (
    <div {...rest} style={{ ...style, position: 'relative' }}>
      {sized && <div aria-hidden style={{ paddingTop: `${100 / ratio}%` }} />}
      <div style={sized ? FILL : undefined}>{children}</div>
    </div>
  );
}
